import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Divider, IconButton, Modal, styled, TextField, Typography, useTheme } from '@mui/material';
import { Bed, Close, Person } from '@mui/icons-material';

import { StandardSmallModalBox } from '@frontend/features/common/utils/modals';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';

import { HotelSearchRoomInputWidget } from './HotelSearchRoomInputWidget';
import { RoomSelection, SearchData } from './types';

const RoomInputTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isMediumUp',
})<{isMediumUp: boolean}>(({ theme }) => ({
  justifyContent: 'flex-start',
  gap: theme.spacing(1),
  borderRadius: theme.spacing(1),
  pointerEvents: 'unset',
  width: '100%',
}));

const BedIcon = styled(Bed)(({ theme }) => ({
  color: theme.palette.primary.main,
  position: 'relative',
  left: theme.spacing(-0.5),
  marginRight: theme.spacing(0.5),
}));

interface HotelSearchRoomInputProps {
  setSearchRooms: (value: RoomSelection[]) => void;
  searchData: SearchData;
}

export const HotelSearchRoomInput: FC<HotelSearchRoomInputProps> = ({ setSearchRooms, searchData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMediumUp } = useBreakpoints();
  const { addToast } = useSnackBar();
  // const { setSearchRooms, searchData} = useHotelSearch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [roomsToPopulate, setRoomsToPopulate] = useState<RoomSelection[]>([]);

  const handleToggleMenu = () => {
    setRoomsToPopulate(searchData.rooms || []);
    setIsMenuOpen((prevState) => !prevState);
  };

  const handleRoomsChange = (rooms: RoomSelection[]) => {
    setRoomsToPopulate(rooms);
  };

  const handleSaveRooms = () => {
    if(roomsToPopulate.every(({ paxes }) => paxes.some((age) => age >= 18))) {
      setSearchRooms(roomsToPopulate);
      handleToggleMenu();
    } else {
      addToast({
        color: 'error',
        message: t('hotels.searcher.room')
      });
    }
  };

  return (
    <>
      <RoomInputTextField
        fullWidth
        role="button"
        onClick={handleToggleMenu}
        placeholder={t('hotels.searcher.room')}
        value= {searchData.rooms && searchData.rooms.length ? t('hotels.roomMenu.selected', { count: searchData.rooms.length }) : ''}
        InputProps={{
          startAdornment: <BedIcon />,
          readOnly: true,
        }}
        isMediumUp={isMediumUp}
        size='small'
      />
      <Modal
        open={isMenuOpen}
        onClose={() => handleToggleMenu()}
      >
        <StandardSmallModalBox
          display="flex"
          flexDirection="column"
          maxHeight="90%"
        >
          <Box display="flex" gap={theme.spacing(2)}>
            <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
              <Person color="disabled" />
              <Typography id="edit-rooms-modal-title" variant="h6" component="h2">
                {t('hotels.searcher.room')}
              </Typography>
            </Box>
            <IconButton onClick={handleToggleMenu}>
              <Close color="primary" />
            </IconButton>
          </Box>
          <HotelSearchRoomInputWidget
            defaultRooms={searchData.rooms}
            onRoomsChange={handleRoomsChange}
          />
          <Divider/>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSaveRooms()}
            sx={{
              margin: theme.spacing(2)
            }}
          >
            {t('hotels.roomMenu.apply')}
          </Button>
        </StandardSmallModalBox>
      </Modal>
    </>
  );
};
